import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import type { MetricIdentity } from "@/metrics/catalog"
import type { MetricAutoAddRuleSource } from "@/metrics/runtime-adapters"
import { RUN_RESULT_STATUSES } from "@/runs/status"
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useHoverOpen,
} from "@coval/ui"
import {
  BarChart3,
  CheckSquare,
  ChevronRight,
  Dna,
  Search,
  X,
} from "lucide-react"

import {
  type MetricValueFilter as MetricFilter,
  type MetricValueFilterOperator as MetricFilterOperator,
} from "@/lib/metricValueFilters"
import { FilterPopover } from "@/components/filters/FilterPopover"
import {
  SearchableCheckboxList,
  type SearchableItem,
} from "@/components/filters/SearchableCheckboxList"
import type { RunRowLike } from "@/types/RunRow"
import { cn } from "@/utils/cn"
import { resolveMetricDisplayName } from "@/utils/formatting/format-metric-display-results"

import {
  getDefaultFilterValue,
  resolveMetricFilterInfo,
} from "./runFilterMetricValues"

export {
  getDefaultFilterValue,
  getMetricFilterInfo,
  resolveMetricFilterInfo,
} from "./runFilterMetricValues"

// Metric Filter Form Component
const MetricFilterForm = ({
  metricRecordsById,
  metricSourcesById,
  runRows,
  onAddFilter,
}: {
  metricRecordsById: Record<string, MetricIdentity>
  metricSourcesById: Record<string, MetricAutoAddRuleSource>
  runRows: RunRowLike[]
  onAddFilter: (
    metricName: string,
    operator: MetricFilterOperator,
    value: number | string | null,
    rangeMax?: number,
  ) => void
}) => {
  const [selectedMetric, setSelectedMetric] = useState<string>("")
  const [selectedOperator, setSelectedOperator] =
    useState<MetricFilterOperator>("=")
  const [filterValue, setFilterValue] = useState<string>("")
  const [rangeMaxValue, setRangeMaxValue] = useState<string>("")

  // Only show metrics that have values in at least one row
  const availableMetrics = useMemo(
    () =>
      Object.entries(metricRecordsById)
        .filter(([_, metric]) => {
          if (!metric.metric_display_name) {
            return false
          }

          // Check if this metric has a value in at least one run row
          return runRows.some(
            (row) =>
              row.metric_outputs?.[metric.id] !== undefined &&
              row.metric_outputs[metric.id] !== null,
          )
        })
        .sort(([_, a], [__, b]) =>
          (a.metric_display_name || "").localeCompare(
            b.metric_display_name || "",
          ),
        ),
    [metricRecordsById, runRows],
  )

  // Determine the type of the selected metric and get its selectable values
  const metricInfo = useMemo(
    () => resolveMetricFilterInfo(metricSourcesById[selectedMetric], runRows),
    [selectedMetric, metricSourcesById, runRows],
  )

  // Get appropriate operators based on metric type
  const availableOperators = useMemo(() => {
    if (metricInfo.type === "string") {
      return [
        { key: "=", label: "=" },
        { key: "!=", label: "≠" },
      ] as const
    }
    return [
      { key: "=", label: "=" },
      { key: "!=", label: "≠" },
      { key: ">", label: ">" },
      { key: ">=", label: "≥" },
      { key: "<", label: "<" },
      { key: "<=", label: "≤" },
      { key: "range", label: "between" },
    ] as const
  }, [metricInfo.type])

  const handleAdd = useCallback(() => {
    if (!selectedMetric) {
      return
    }

    if (selectedOperator === "exists") {
      onAddFilter(selectedMetric, selectedOperator, null)
    } else if (selectedOperator === "range") {
      const minValue = parseFloat(filterValue)
      const maxValue = parseFloat(rangeMaxValue)
      if (isNaN(minValue) || isNaN(maxValue) || minValue > maxValue) {
        return
      }
      onAddFilter(selectedMetric, selectedOperator, minValue, maxValue)
    } else if (metricInfo.type === "string") {
      if (!filterValue) {
        return
      }
      onAddFilter(selectedMetric, selectedOperator, filterValue)
    } else {
      const numValue = parseFloat(filterValue)
      if (isNaN(numValue)) {
        return
      }
      onAddFilter(selectedMetric, selectedOperator, numValue)
    }

    // Reset form
    setSelectedMetric("")
    setSelectedOperator("=")
    setFilterValue("")
    setRangeMaxValue("")
  }, [
    selectedMetric,
    selectedOperator,
    filterValue,
    rangeMaxValue,
    metricInfo.type,
    onAddFilter,
  ])

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label htmlFor="metric-filter-select" className="text-xs">
          Metric
        </Label>
        <Select
          value={selectedMetric}
          onValueChange={(value) => {
            setSelectedMetric(value)
            setSelectedOperator("=")
            setFilterValue(
              getDefaultFilterValue(
                resolveMetricFilterInfo(metricSourcesById[value], runRows),
              ),
            )
            setRangeMaxValue("")
          }}
        >
          <SelectTrigger aria-label="Metric" id="metric-filter-select">
            <SelectValue placeholder="Select a metric" />
          </SelectTrigger>
          <SelectContent>
            {availableMetrics.map(([metricName, metric]) => (
              <SelectItem key={metricName} value={metricName}>
                {resolveMetricDisplayName(
                  metric.metric_display_name,
                  metricName,
                )}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex gap-2">
        <div className="space-y-1 w-32">
          <Label htmlFor="operator-filter-select" className="text-xs">
            Operator
          </Label>
          <Select
            value={selectedOperator}
            onValueChange={(value) =>
              setSelectedOperator(value as MetricFilterOperator)
            }
          >
            <SelectTrigger aria-label="Operator" id="operator-filter-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availableOperators.map((op) => (
                <SelectItem key={op.key} value={op.key}>
                  {op.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {selectedOperator !== "exists" &&
          (metricInfo.type === "string" ? (
            <div className="space-y-1 flex-1">
              <Label htmlFor="value-filter-select" className="text-xs">
                Value
              </Label>
              <Select value={filterValue} onValueChange={setFilterValue}>
                <SelectTrigger aria-label="Value" id="value-filter-select">
                  <SelectValue placeholder="Select value" />
                </SelectTrigger>
                <SelectContent>
                  {metricInfo.uniqueValues.map((value) => (
                    <SelectItem key={value} value={value}>
                      {value}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : selectedOperator === "range" ? (
            <>
              <div className="space-y-1 flex-1">
                <Label htmlFor="filter-min" className="text-xs">
                  Min
                </Label>
                <Input
                  id="filter-min"
                  type="number"
                  value={filterValue}
                  onChange={(e) => setFilterValue(e.target.value)}
                  placeholder="0"
                />
              </div>
              <div className="space-y-1 flex-1">
                <Label htmlFor="filter-max" className="text-xs">
                  Max
                </Label>
                <Input
                  id="filter-max"
                  type="number"
                  value={rangeMaxValue}
                  onChange={(e) => setRangeMaxValue(e.target.value)}
                  placeholder="0"
                />
              </div>
            </>
          ) : (
            <div className="space-y-1 flex-1">
              <Label htmlFor="filter-value" className="text-xs">
                Value
              </Label>
              <Input
                id="filter-value"
                type="number"
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)}
                placeholder="0"
              />
            </div>
          ))}
      </div>

      <Button
        variant="secondary"
        onClick={handleAdd}
        disabled={
          !selectedMetric ||
          (selectedOperator !== "exists" &&
            (selectedOperator === "range"
              ? !filterValue ||
                isNaN(parseFloat(filterValue)) ||
                !rangeMaxValue ||
                isNaN(parseFloat(rangeMaxValue)) ||
                parseFloat(filterValue) > parseFloat(rangeMaxValue)
              : metricInfo.type === "string"
                ? !filterValue
                : !filterValue || isNaN(parseFloat(filterValue))))
        }
        className="w-full"
      >
        Add Filter
      </Button>
    </div>
  )
}

// Mutations Filter Section - uses mutation IDs under the hood (same pattern as metrics)
export const MutationsFilterSection = ({
  isOpen,
  onOpenChange,
  selectedMutationIds,
  onMutationFilterChange,
  mutationNameById,
}: {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  /** Selected mutation IDs (IDs under the hood). */
  selectedMutationIds: string[]
  onMutationFilterChange: (ids: string[]) => void
  /** Mutation id -> display name (from run rows). */
  mutationNameById: Map<string, string>
}) => {
  const hoverProps = useHoverOpen({ open: isOpen, onOpenChange })
  const [searchQuery, setSearchQuery] = useState("")
  const searchInputRef = useRef<HTMLInputElement>(null)

  const mutations = useMemo(
    () =>
      Array.from(mutationNameById.entries()).map(([id, display_name]) => ({
        id,
        display_name,
      })),
    [mutationNameById],
  )

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      const id = setTimeout(() => searchInputRef.current?.focus(), 100)
      return () => clearTimeout(id)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("")
    }
  }, [isOpen])

  const filteredMutations = useMemo(() => {
    if (!searchQuery.trim()) {
      return mutations
    }
    return mutations?.filter((mutation) =>
      mutation.display_name?.toLowerCase().includes(searchQuery.toLowerCase()),
    )
  }, [mutations, searchQuery])

  return (
    <FilterPopover
      isOpen={isOpen}
      onToggle={() => onOpenChange(!isOpen)}
      trigger={
        <button
          type="button"
          {...hoverProps}
          className={cn(
            "flex w-full items-center justify-between px-2 py-2 rounded-md transition-colors cursor-pointer",
            "hover:bg-surface-1",
            selectedMutationIds.length > 0 && "bg-surface-1",
          )}
        >
          <div className="flex items-center gap-2 mr-3">
            <Dna
              className={cn(
                "w-4 h-4",
                selectedMutationIds.length > 0
                  ? "text-coval-info-text"
                  : "text-coval-text-muted",
              )}
            />
            <span
              className={cn(
                "text-sm font-medium",
                selectedMutationIds.length > 0
                  ? "text-coval-info-text"
                  : "text-coval-text",
              )}
            >
              Mutations
            </span>
          </div>
          <ChevronRight className="w-3 h-3 text-coval-text-muted" />
        </button>
      }
    >
      <Card className="border-0 shadow-none">
        <CardContent className="p-0 w-full">
          <div className="p-3 border-b border-coval-border">
            <Input
              ref={searchInputRef}
              aria-label="Search mutations"
              placeholder="Search mutations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onClear={() => setSearchQuery("")}
              startAdornment={<Search className="w-4 h-4" />}
            />
          </div>

          <div className="max-h-[50vh] overflow-y-auto">
            <div className="w-full p-1.5">
              <div className="flex flex-col">
                {filteredMutations?.map((mutation) => (
                  <label
                    key={mutation.id}
                    className="flex max-w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 hover:bg-surface-contrast"
                  >
                    <Checkbox
                      checked={selectedMutationIds.includes(mutation.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          onMutationFilterChange([
                            ...selectedMutationIds,
                            mutation.id,
                          ])
                        } else {
                          onMutationFilterChange(
                            selectedMutationIds.filter(
                              (id) => id !== mutation.id,
                            ),
                          )
                        }
                      }}
                    />
                    <span className="text-sm block truncate max-w-[200px]">
                      {mutation.display_name || mutation.id}
                    </span>
                  </label>
                ))}
                {filteredMutations?.length === 0 && (
                  <div className="px-3 py-4 text-center text-sm text-coval-text-muted">
                    No mutations found
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </FilterPopover>
  )
}

// Metrics section (metric filters, same UI as MetricFilterDropdown)
export const MetricsSection = ({
  isOpen,
  onOpenChange,
  metricRecordsById,
  metricSourcesById,
  runRows,
  filters,
  onAddFilter,
  onRemoveFilter,
  onClearAllFilters,
}: {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  metricRecordsById: Record<string, MetricIdentity>
  metricSourcesById: Record<string, MetricAutoAddRuleSource>
  runRows: RunRowLike[]
  filters: MetricFilter[]
  onAddFilter: (
    metricName: string,
    operator: MetricFilterOperator,
    value: number | string | null,
    rangeMax?: number,
  ) => void
  onRemoveFilter: (index: number) => void
  onClearAllFilters: () => void
}) => {
  const hoverProps = useHoverOpen({ open: isOpen, onOpenChange })

  return (
    <FilterPopover
      isOpen={isOpen}
      onToggle={() => onOpenChange(!isOpen)}
      trigger={
        <button
          type="button"
          {...hoverProps}
          className={cn(
            "flex w-full items-center justify-between px-2 py-2 rounded-md transition-colors cursor-pointer",
            "hover:bg-surface-1",
            filters.length > 0 && "bg-surface-1",
          )}
        >
          <div className="flex items-center gap-2">
            <BarChart3
              className={cn(
                "w-4 h-4",
                filters.length > 0
                  ? "text-coval-info-text"
                  : "text-coval-text-muted",
              )}
            />
            <span
              className={cn(
                "text-sm font-medium",
                filters.length > 0 ? "text-coval-info-text" : "text-coval-text",
              )}
            >
              Metrics
            </span>
          </div>
          <ChevronRight className="w-3 h-3 text-coval-text-muted" />
        </button>
      }
    >
      <Card className="border-0 shadow-none">
        <CardContent className="p-4 space-y-4 w-full">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold">Metric Filters</h4>
            {filters.length > 0 && (
              <Button variant="ghost" onClick={onClearAllFilters}>
                Clear All
              </Button>
            )}
          </div>

          <MetricFilterForm
            metricRecordsById={metricRecordsById}
            metricSourcesById={metricSourcesById}
            runRows={runRows}
            onAddFilter={onAddFilter}
          />

          {filters.length > 0 && (
            <>
              <Divider />
              <div className="space-y-2">
                <div className="text-xs font-semibold text-coval-text-muted">
                  Active Filters
                </div>
                {filters.map((filter, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2 bg-surface-1 rounded-lg"
                  >
                    <div className="flex flex-col flex-1">
                      <span className="text-sm font-medium">
                        {resolveMetricDisplayName(
                          metricRecordsById[filter.metricName]
                            ?.metric_display_name,
                          filter.metricName,
                        )}
                      </span>
                      <span className="text-xs text-coval-text-muted">
                        {filter.operator === "exists"
                          ? "Has value"
                          : filter.operator === "range"
                            ? `${filter.value} – ${filter.rangeMax}`
                            : `${filter.operator} ${filter.value}`}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      icon
                      onClick={() => onRemoveFilter(index)}
                      tooltip="Remove filter"
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </FilterPopover>
  )
}

// Status filter section - use the shared result-row status vocabulary so filtering matches row.error_status.
const STATUS_ITEMS: SearchableItem[] = RUN_RESULT_STATUSES.map((status) => ({
  id: status,
  label: status.toLowerCase(),
  searchValue: status.toLowerCase(),
}))

export const StatusFilterSection = ({
  isOpen,
  onOpenChange,
  selectedStatuses,
  onStatusFilterChange,
}: {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  selectedStatuses: string[]
  onStatusFilterChange: (statuses: string[]) => void
}) => {
  const hoverProps = useHoverOpen({ open: isOpen, onOpenChange })

  return (
    <FilterPopover
      isOpen={isOpen}
      onToggle={() => onOpenChange(!isOpen)}
      trigger={
        <button
          type="button"
          {...hoverProps}
          className={cn(
            "flex w-full items-center justify-between px-2 py-2 rounded-md transition-colors cursor-pointer",
            "hover:bg-surface-1",
            selectedStatuses.length > 0 && "bg-surface-1",
          )}
        >
          <div className="flex items-center gap-2 mr-3">
            <CheckSquare
              className={cn(
                "w-4 h-4",
                selectedStatuses.length > 0
                  ? "text-coval-info-text"
                  : "text-coval-text-muted",
              )}
            />
            <span
              className={cn(
                "text-sm font-medium",
                selectedStatuses.length > 0
                  ? "text-coval-info-text"
                  : "text-coval-text",
              )}
            >
              Status
            </span>
          </div>
          <ChevronRight className="w-3 h-3 text-coval-text-muted" />
        </button>
      }
    >
      <SearchableCheckboxList
        items={STATUS_ITEMS}
        selectedIds={selectedStatuses}
        onSelectionChange={onStatusFilterChange}
        isLoading={false}
        isOpen={isOpen}
        placeholder="Search statuses..."
        emptyMessage="No statuses found"
      />
    </FilterPopover>
  )
}
